import { Auth } from 'aws-amplify';
import * as types from './types';
import * as rootTypes from '@/store/types';

const actions = {
  [types.SIGN_IN]: (context, payload) => new Promise((resolve, reject) => {
    context.commit(types.SET_USER, {
      email: payload.email.toLowerCase(),
    });
    return Auth
      .signIn({
        username: payload.email,
        password: payload.password,
      }).then((data) => {
        if (data.challengeName === 'NEW_PASSWORD_REQUIRED') {
          resolve('/new-password');
        } else if (data.challengeName === 'CUSTOM_CHALLENGE') {
          Auth.sendCustomChallengeAnswer(data, payload.code)
            .then((user) => {
              context.commit(types.SET_USER, {
                email: payload.email.toLowerCase(),
                password: payload.password,
                data: user,
              });
              return resolve();
            })
            .catch(() => {
              reject();
            });
        } else {
          context.commit(types.SET_USER, {
            email: payload.email.toLowerCase(),
            password: payload.password,
            data,
          });
          return resolve('/dashboard');
        }
      })
      .catch((error) => {
        if (error.code === 'PasswordResetRequiredException') {
          const errorMessage = "You're required to change your password";
          context.commit(
            rootTypes.SET_GLOBAL_MESSAGE, {
              message: errorMessage,
              type: 'danger',
            }, { root: true },
          );
          resolve('/forgot-password');
        } else if (error.code === 'UserNotConfirmedException') {
          resolve('/confirm');
        } else if (payload.ignoreError) {
          reject();
        } else {
          const errorMessage = 'Invalid email or password';
          context.commit(
            rootTypes.SET_GLOBAL_MESSAGE, {
              message: errorMessage,
              type: 'danger',
            }, { root: true },
          );
          reject(new Error(errorMessage));
        }
      });
  }),
  [types.SIGN_UP]: (context, payload) => new Promise((resolve, reject) => Auth.signUp({
    username: payload.email.toLowerCase(),
    password: payload.password,
    attributes: {
      email: payload.email.toLowerCase(),
      'custom:first_name': payload.firstName ? payload.firstName : '',
      'custom:last_name': payload.lastName ? payload.lastName : '',
      'custom:company_name': payload.companyName ? payload.companyName : '',
      'custom:sign_up_type': payload.signUpType ? payload.signUpType : 'USER',
      'custom:auth_code': payload.authCode ? payload.authCode : '',
      'custom:email_properties': payload.emailProperties ? payload.emailProperties : '{}',
    },
    validationData: [],
  })
    .then((data) => {
      if (!payload.signUpType || payload.signUpType !== 'SHARE') {
        context.commit(rootTypes.SET_GLOBAL_MESSAGE, {
          message: 'Account was created successfully',
          type: 'success',
        }, { root: true });
        context.commit(types.SET_USER, {
          email: payload.email.toLowerCase(),
          password: payload.password,
          data,
        });
      }
      resolve();
    })
    .catch((error) => {
      let errorMessage;
      if (error.code === 'InvalidPasswordException' || error.code === 'InvalidParameterException') {
        errorMessage = 'Password does not meet requirements';
      } else {
        errorMessage = error.message;
      }
      context.commit(
        rootTypes.SET_GLOBAL_MESSAGE, {
          message: errorMessage,
          type: 'danger',
        }, { root: true },
      );
      reject();
    })),
  [types.CONFIRM_SIGN_UP]: (context, payload) => new Promise((resolve, reject) => Auth
    .confirmSignUp(payload.email.toLowerCase(), payload.code)
    .then(() => {
      resolve();
    })
    .catch(() => {
      reject();
    })),
  [types.RESEND_SIGN_UP]: (context, payload) => Auth.resendSignUp(payload)
    .then(() => {
      context.commit(rootTypes.SET_GLOBAL_MESSAGE, {
        message: 'Verification code has been sent',
        type: 'success',
      }, { root: true });
    })
    .catch(() => {
      const errorMessage = 'Credentials are incorrect';
      context.commit(
        rootTypes.SET_GLOBAL_MESSAGE, {
          message: errorMessage,
          type: 'danger',
        }, { root: true },
      );
    }),
  [types.FORGOT_PASSWORD]: (context, payload) => new Promise((resolve) => {
    context.commit(types.SET_USER, {
      email: payload.toLowerCase(),
    });

    return Auth
      .forgotPassword(payload.toLowerCase())
      .then(() => {
        context.commit(rootTypes.SET_GLOBAL_MESSAGE, {
          message: 'Email was sent, please check your inbox',
          type: 'success',
        }, { root: true });
        resolve('/confirm-forgotten-password');
      })
      .catch((error) => {
      // Do this if the user has not confirmed their account.
        // There is no more accurate way to determine that the email is unverified
        if (error.code === 'InvalidParameterException') {
          resolve('/confirm');
        }
      });
  }),
  [types.CONFIRM_FORGOT_PASSWORD]: (context, payload) => new Promise((resolve, reject) => Auth
    .forgotPasswordSubmit(payload.email, payload.code, payload.newPassword)
    .then((data) => {
      context.commit(types.SET_USER, {
        email: payload.email.toLowerCase(),
        password: payload.newPassword,
        data,
      });
      resolve();
    })
    .catch(async (error) => {
      if (error.code === 'ExpiredCodeException') {
        context.commit(rootTypes.SET_GLOBAL_MESSAGE, {
          message: 'Your link has expired. Please reset your password again.',
          type: 'danger',
        }, { root: true });
        resolve();
      } else {
        const errorMessage = 'Credentials are incorrect';
        context.commit(
          rootTypes.SET_GLOBAL_MESSAGE, {
            message: errorMessage,
            type: 'danger',
          }, { root: true },
        );
        reject();
      }
    })),
  [types.COMPLETE_NEW_PASSWORD]: (context, payload) => new Promise(
    (resolve, reject) => {
      const { user, password } = payload;
      return Auth
        .completeNewPassword(user, password)
        .then(() => {
          resolve();
        })
        .catch(() => {
          const errorMessage = 'Credentials are incorrect';
          context.commit(
            rootTypes.SET_GLOBAL_MESSAGE, {
              message: errorMessage,
              type: 'danger',
            }, { root: true },
          );
          reject();
        });
    },
  ),
  [types.CHANGE_PASSWORD]: (context, payload) => new Promise(
    (resolve, reject) => {
      const { user, oldPassword, newPassword } = payload;
      return Auth
        .changePassword(user, oldPassword, newPassword)
        .then(() => {
          resolve();
        })
        .catch(() => {
          const errorMessage = 'Credentials are incorrect';
          context.commit(
            rootTypes.SET_GLOBAL_MESSAGE, {
              message: errorMessage,
              type: 'danger',
            }, { root: true },
          );
          reject();
        });
    },
  ),
  [types.SIGN_OUT]: (context) => new Promise(
    (resolve) => Auth.signOut({ global: true })
      .then(() => {
        context.commit(rootTypes.RESET_ALL_STATE, null, { root: true });
        resolve();
      })
      .catch(() => {
        resolve();
      }),
  ),
};

export default actions;
